import React, {useEffect, useState} from "react";
import PropTypes from 'prop-types';
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import CurrencyFormatter from "currency-formatter";
import Errors from "../../../utils/Errors";
import {FormattedMessage, useIntl} from "react-intl";
import IncentivesField from "./IncentivesField";
import StyleableSection from "../../../components/StyleableSection";
import AmountSection from "./AmountSection";
import {
  Button,
  ButtonGroup,
  Checkbox, Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  OutlinedInput,
  Typography,
  useMediaQuery,
  withStyles
} from "@material-ui/core";
import TextField from '../../../components/TextField';
import DeliverTimes from "../../../constants/DeliverTimes";
import DateFnsUtils from '@date-io/date-fns';
import {KeyboardDatePicker, KeyboardTimePicker, MuiPickersUtilsProvider,} from '@material-ui/pickers';
import CardDesignSection from "./CardDesignSection";
import InputAdornment from "@material-ui/core/InputAdornment";
import DateIcon from '@material-ui/icons/Event';
import TimeIcon from '@material-ui/icons/AccessTime';
import {useTheme} from "@material-ui/core/styles";
import TextSection from "../../../components/TextSection";
import {red} from "@material-ui/core/colors";
import ActionSection from "./ActionSection";
import CheckoutActions from "../../../constants/CheckoutActions";
import formatMoney from "../../../utils/formatMoney";

OrderForm.propTypes = {
  cardDesigns: PropTypes.array.isRequired,
  selectedCardDesignIndex: PropTypes.number.isRequired,
  senderName: PropTypes.string,
  senderEmail: PropTypes.string,
  sendToSender: PropTypes.bool,
  recipientName: PropTypes.string,
  recipientEmail: PropTypes.string,
  message: PropTypes.string,
  deliveryTimeType: PropTypes.string,
  deliveryTimeTime: PropTypes.object,
  deliveryDateTime: PropTypes.object,
  amount: PropTypes.number,
  balance: PropTypes.number,
  currencyCode: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  checkoutSettings: PropTypes.object.isRequired,
  presetAmounts: PropTypes.array.isRequired,
  customAmountAllowed: PropTypes.bool.isRequired,
  cardDesign: PropTypes.object,
  incentives: PropTypes.array,
  applicableIncentive: PropTypes.object,
  theme: PropTypes.object,
  errors: PropTypes.instanceOf(Errors).isRequired,
  shopifyMode: PropTypes.bool.isRequired,
  shopifySettings: PropTypes.object,
  customSettings: PropTypes.object,
  action: PropTypes.string.isRequired,
  giftCardNumber: PropTypes.string,
  giftCardDetails: PropTypes.object,
  onCardDesignChange: PropTypes.func.isRequired,
  onAmountChange: PropTypes.func.isRequired,
  onRecipientNameChange: PropTypes.func.isRequired,
  onRecipientEmailChange: PropTypes.func.isRequired,
  onSenderNameChange: PropTypes.func.isRequired,
  onSenderEmailChange: PropTypes.func.isRequired,
  onMessageChange: PropTypes.func.isRequired,
  onSendToSenderChange: PropTypes.func.isRequired,
  onDeliveryTimeTypeChange: PropTypes.func.isRequired,
  onDeliveryDateTimeChange: PropTypes.func.isRequired,
  onCheckoutActionChange: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onGiftCardNumberChange: PropTypes.func.isRequired,
  onCheckBalance: PropTypes.func.isRequired,
  classes: PropTypes.object
};

function OrderForm({
                     cardDesigns,
                     selectedCardDesignIndex,
                     amount,
                     balance,
                     senderName,
                     senderEmail,
                     message,
                     sendToSender,
                     recipientName,
                     recipientEmail,
                     deliveryDateTime,
                     deliveryTimeType,
                     currencyCode,
                     incentives,
                     applicableIncentive,
                     cardDesign,
                     locale,
                     checkoutSettings,
                     presetAmounts,
                     customAmountAllowed,
                     theme,
                     classes,
                     errors,
                     shopifyMode,
                     shopifySettings,
                     customSettings,
                     action,
                     giftCardNumber,
                     giftCardDetails,
                     onCardDesignChange,
                     onAmountChange,
                     onSenderNameChange,
                     onSenderEmailChange,
                     onRecipientNameChange,
                     onRecipientEmailChange,
                     onMessageChange,
                     onSendToSenderChange,
                     onDeliveryTimeTypeChange,
                     onDeliveryDateTimeChange,
                     onCheckoutActionChange,
                     onContinue,
                     onGiftCardNumberChange,
                     onCheckBalance
                   }) {

  const intl = useIntl();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);

  useEffect(() => {
    if(customSettings && customSettings.scd) {
      let defaultSelectedCardDesignIndex = cardDesigns.findIndex((cardDesign) => cardDesign.id === customSettings.scd);
      if(defaultSelectedCardDesignIndex > -1) {
        onCardDesignChange(cardDesigns[defaultSelectedCardDesignIndex], defaultSelectedCardDesignIndex);
      }
    }
  }, []);

  function handleSenderNameChange(e) {
    onSenderNameChange(e.target.value);
  }

  function handleSenderEmailChange(e) {
    onSenderEmailChange(e.target.value);
  }

  function handleMessageChange(e) {
    onMessageChange(e.target.value);
  }

  function handleAmountChange(amount) {
    onAmountChange(amount);
  }

  function handleRecipientNameChange(e) {
    onRecipientNameChange(e.target.value);
  }

  function handleRecipientEmailChange(e) {
    onRecipientEmailChange(e.target.value);
  }

  function handleSendToSenderChange(e) {
    onSendToSenderChange(e.target.checked);
  }

  function handleOnContinueClick() {
    if (!errors.hasError("amount")) {
      onContinue();
    }
  }

  function handleGiftCardNumberChange(e) {
    onGiftCardNumberChange(e.target.value);
  }

  function handleCheckBalance() {
    onCheckBalance();
  }

  function renderContinueButtonLabel() {
    if (shopifyMode) {
      if (shopifySettings && shopifySettings.buttonLabel) {
        return shopifySettings.buttonLabel;
      }
      return (
        <FormattedMessage id={"button.add-to-cart"}/>
      );
    }
    return (
      <FormattedMessage id={"button.continue"}/>
    );
  }

  function DateField({...props}) {
    return (
      <FormControl {...props} >
        <InputLabel
          htmlFor="delivery-date-picker"
          shrink={false}
          disableAnimation={true}
          className={classes.datetimePickerLabel}
          variant="outlined"
          classes={{root: classes.datetimePickerLabel}}>
          <FormattedMessage id={'form.delivery-date'}/>
        </InputLabel>
        <OutlinedInput
          {...props}
          classes={{input: classes.datetimePickerInput}}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => setDatePickerOpen(true)}
              >
                <DateIcon/>
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
    );
  }

  function TimeField({...props}) {
    return (
      <FormControl {...props} >
        <InputLabel
          htmlFor="delivery-time-picker"
          shrink={false}
          disableAnimation={true}
          className={classes.datetimePickerLabel}
          variant="outlined"
          classes={{root: classes.datetimePickerLabel}}>
          <FormattedMessage id={'form.delivery-time'}/>
        </InputLabel>
        <OutlinedInput
          {...props}
          classes={{input: classes.datetimePickerInput}}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => setTimePickerOpen(true)}
              >
                <TimeIcon/>
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
    );
  }

  function TermsInHeader() {
    if(checkoutSettings.loopzFooterDisabled) {
      return null;
    }
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('md'));

    if (matches) {
      return (
        <div className={classes.termsContainer}>
          <Typography variant="caption" className={classes.terms}>
            <FormattedMessage id={'label.powered-by'}/>
            <Link href={'https://www.loopz.io'} target="_blank" className={classes.link}>Loopz Gift Cards</Link>
            |
            <Link href={'https://www.loopz.io/terms-of-use'} target="_blank" className={classes.link}><FormattedMessage
              id={'label.terms'}/></Link>
            <Link href={'https://www.loopz.io/privacy-policy'} target="_blank" className={classes.link}><FormattedMessage
              id={'label.privacy'}/></Link>
          </Typography>
        </div>
      );
    }
    return null;
  }

  function TermsInForm() {
    if(checkoutSettings.loopzFooterDisabled) {
      return null;
    }
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('sm'));

    if (matches) {
      return (
        <div className={classes.termsContainer}>
          <Typography variant="caption" className={classes.termsCentered}>
            <FormattedMessage id={'label.powered-by'}/>
            <Link href={'https://www.loopz.io'} target="_blank" className={classes.link}>Loopz Gift Cards</Link>
          </Typography>
          <Typography variant="caption" className={classes.termsCentered}>
            <Link href={'https://www.loopz.io/terms-of-use'} target="_blank" className={classes.link}><FormattedMessage
              id={'label.terms'}/></Link>
            <Link href={'https://www.loopz.io/privacy-policy'} target="_blank" className={classes.link}><FormattedMessage
              id={'label.privacy'}/></Link>
          </Typography>
        </div>
      );
    }
    return null;
  }

  function renderNewCardForm() {
    return (
      <>
        <Grid item xs={12}>
          <Typography variant={'subtitle2'} className={classes.amountSubtitle}>
            <FormattedMessage id={'header.gift-amount'}/>
          </Typography>
          <AmountSection
            amount={amount}
            cardDesign={cardDesign}
            currency={currency}
            section={checkoutSettings.amount}
            presetBalances={presetAmounts}
            customAmountAllowed={customAmountAllowed}
            locale={locale}
            onChange={handleAmountChange}
            errors={errors}/>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={<FormattedMessage id={"placeholder.sender-name"}/>}
            placeholder={intl.formatMessage({id: "placeholder.sender-name"})}
            fullWidth={true}
            value={senderName}
            InputLabelProps={{
              shrink: false
            }}
            variant={'outlined'}
            onChange={handleSenderNameChange}
            data-test={'sender-name'}
            error={errors.hasError('senderName')}
            helperText={errors.getError('senderName') && <FormattedMessage id={errors.getError('senderName')}/>}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={<FormattedMessage id={"placeholder.sender-email"}/>}
            placeholder={intl.formatMessage({id: "placeholder.sender-email"})}
            fullWidth={true}
            value={senderEmail}
            variant={'outlined'}
            InputLabelProps={{
              shrink: true
            }}
            data-test={'sender-email'}
            onChange={handleSenderEmailChange}
            error={errors.hasError('senderEmail')}
            helperText={errors.getError('senderEmail') && <FormattedMessage id={errors.getError('senderEmail')}/>}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendToSender}
                onChange={handleSendToSenderChange}
                name="sendToSender"
                color="primary"
              />
            }
            label={<FormattedMessage id={"label.send-to-sender"}/>}
          />
        </Grid>
        {!sendToSender &&
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                label={<FormattedMessage id={"form.recipient-name"}/>}
                placeholder={intl.formatMessage({id: "placeholder.recipient-name"})}
                fullWidth={true}
                value={recipientName}
                InputLabelProps={{
                  shrink: true
                }}
                variant={'outlined'}
                onChange={handleRecipientNameChange}
                data-test={'recipient-name'}
                error={errors.hasError('recipientName')}
                helperText={errors.getError('recipientName') &&
                  <FormattedMessage id={errors.getError('recipientName')}/>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={<FormattedMessage id={"form.recipient-email"}/>}
                placeholder={intl.formatMessage({id: "placeholder.recipient-email"})}
                fullWidth={true}
                value={recipientEmail}
                InputLabelProps={{
                  shrink: true
                }}
                variant={'outlined'}
                data-test={'recipient-email'}
                onChange={handleRecipientEmailChange}
                error={errors.hasError('recipientEmail')}
                helperText={errors.getError('recipientEmail') &&
                  <FormattedMessage id={errors.getError('recipientEmail')}/>}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                label={<FormattedMessage id={"form.message"}/>}
                rows={2}
                multiline={true}
                rowsMax={5}
                placeholder={intl.formatMessage({id: "placeholder.message"})}
                fullWidth={true}
                value={message}
                InputLabelProps={{
                  shrink: true
                }}

                variant={'outlined'}
                onChange={handleMessageChange}
                data-test={'message'}
                error={errors.hasError('message')}
                helperText={errors.getError('message') && <FormattedMessage id={errors.getError('message')}/>}
              />
            </Grid>
          </>
        }
        <Grid item xs={12}>
          <ButtonGroup color="primary" style={{width: '100%'}}>
            <Button
              variant={deliveryTimeType === DeliverTimes.NOW ? 'contained' : 'outlined'}
              color={deliveryTimeType === DeliverTimes.NOW ? 'secondary' : 'default'}
              fullWidth={false}
              onClick={() => onDeliveryTimeTypeChange(DeliverTimes.NOW)}
              style={{width: '100%'}}
              data-test={'button-delivery-instant'}
            >
              <FormattedMessage id={"button.send-instantly"}/>
            </Button>
            <Button
              variant={deliveryTimeType === DeliverTimes.FUTURE ? 'contained' : 'outlined'}
              color={deliveryTimeType === DeliverTimes.FUTURE ? 'secondary' : 'default'}
              fullWidth={false}
              onClick={() => onDeliveryTimeTypeChange(DeliverTimes.FUTURE)}
              style={{width: '100%'}}
              data-test={'button-delivery-later'}
            >
              <FormattedMessage id={"button.send-later"}/>
            </Button>
          </ButtonGroup>
        </Grid>
        {deliveryTimeType === DeliverTimes.FUTURE &&
          <>
            <Grid item xs={12} sm={6}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  id="delivery-date-picker"
                  format="d MMM, yyyy"
                  inputVariant={'outlined'}
                  autoOk
                  fullWidth={true}
                  value={deliveryDateTime}
                  minDate={new Date()}
                  onChange={onDeliveryDateTimeChange}
                  open={datePickerOpen}
                  onClose={() => setDatePickerOpen(false)}
                  TextFieldComponent={DateField}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardTimePicker
                  id="delivery-time-picker"
                  inputVariant={'outlined'}
                  autoOk
                  fullWidth={true}
                  value={deliveryDateTime}
                  onChange={onDeliveryDateTimeChange}
                  open={timePickerOpen}
                  onClose={() => setTimePickerOpen(false)}
                  TextFieldComponent={TimeField}
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </>
        }
      </>
    );
  }

  function renderReloadCardForm() {
    return (
      <>
        <Grid item xs={12}>
          <TextField
            label={<FormattedMessage id={"form.gift-card-number"}/>}
            placeholder={intl.formatMessage({id: "placeholder.gift-card-number"})}
            fullWidth={true}
            value={giftCardNumber}
            InputLabelProps={{
              shrink: true
            }}
            variant={'outlined'}
            data-test={'gift-card-number'}
            onChange={handleGiftCardNumberChange}
            error={errors.hasError('giftCardNumber')}
            helperText={errors.getError('giftCardNumber') &&
              <FormattedMessage id={errors.getError('giftCardNumber')}/>}
            InputProps={{
              endAdornment:
                <InputAdornment position="end">
                  {giftCardDetails &&
                    <Chip
                      variant={'outlined'}
                      color={'default'}
                      label={intl.formatMessage({id: "label.gift-card-balance"}) + ': ' + formatMoney(giftCardDetails.balance.amount, giftCardDetails.balance.currency)}
                    />
                  }
                  {!giftCardDetails &&
                    <Button
                      variant={'outlined'}
                      color={'secondary'}
                      onClick={handleCheckBalance}
                    >
                      <FormattedMessage id={'button.check-balance'}/>
                    </Button>
                  }

                </InputAdornment>
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant={'subtitle2'} className={classes.amountSubtitle}>
            <FormattedMessage id={'header.amount-to-add-to-card'}/>
          </Typography>
          <AmountSection
            amount={amount}
            cardDesign={cardDesign}
            currency={currency}
            section={checkoutSettings.amount}
            presetBalances={presetAmounts}
            customAmountAllowed={customAmountAllowed}
            locale={locale}
            onChange={handleAmountChange}
            errors={errors}/>
        </Grid>

      </>
    );
  }

  const currency = CurrencyFormatter.findCurrency(currencyCode);

  let availableCardDesignIds = cardDesigns.map((cardDesign) => cardDesign.id);
  if(customSettings && customSettings.cd) {
    let customAvailableCardDesignIds = customSettings.cd.split(',');
    customAvailableCardDesignIds = customAvailableCardDesignIds.filter((cardDesignId) => availableCardDesignIds.includes(cardDesignId));
    if(customAvailableCardDesignIds && customAvailableCardDesignIds.length > 0) {
      availableCardDesignIds = customAvailableCardDesignIds;
    }
  }

  return (
    <>
      <Grid container>
        <Grid item xs={12} className={classes.title}>
          <TextSection section={checkoutSettings.title} locale={locale}/>
        </Grid>
        <Grid item xs={12} md={6} className={classes.headerWrapper}>
            <StyleableSection className={classes.headerContainer} section={checkoutSettings.header}>
              <div className={classes.headerLayoutContainer}>
                <CardDesignSection
                  locale={locale}
                  checkoutSettings={checkoutSettings}
                  cardDesigns={cardDesigns}
                  availableCardDesignIds={availableCardDesignIds}
                  selectedCardDesignIndex={selectedCardDesignIndex}
                  currencyCode={currencyCode}
                  amount={balance}
                  theme={theme}
                  onCardDesignChange={onCardDesignChange}
                />
                <div className={classes.incentivesContainer}>
                  <IncentivesField
                    incentives={incentives}
                    applicableIncentive={applicableIncentive}/>
                </div>

                <TermsInHeader/>
              </div>
            </StyleableSection>
        </Grid>
        <Grid item xs={12} md={6} className={classes.formWrapper}>
          <StyleableSection className={classes.formContainer} section={checkoutSettings.form}>
            <div className={classes.formLayoutContainer}>
              <Grid className={classes.form} container spacing={2}>
              {!shopifyMode &&
                <Grid item xs={12}>
                  <ActionSection
                    action={action}
                    section={checkoutSettings.amount}
                    locale={locale}
                    onChange={onCheckoutActionChange}
                  />
                </Grid>
              }
              {action === CheckoutActions.NEW_CARD && renderNewCardForm()}
              {action === CheckoutActions.RELOAD_CARD && renderReloadCardForm()}
              <Grid item xs={12} className={classes.continueButtonContainer}>
                <Button
                  variant={'contained'}
                  color={'primary'}
                  fullWidth={true}
                  size={'large'}
                  data-test={'button-config-next'}
                  onClick={handleOnContinueClick}
                >
                  {renderContinueButtonLabel()}
                </Button>
                <TermsInForm/>
              </Grid>
            </Grid>
            </div>
          </StyleableSection>
        </Grid>
      </Grid>
    </>
  );
}

const styles = (theme) => ({
  headerWrapper: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'flex-end'
    }
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('md')]: {
      alignItems: 'center',
    },
    [theme.breakpoints.up('md')]: {
      alignItems: 'flex-end',
    },
    [theme.breakpoints.up('md')]: {
      width: 500,
      marginRight: theme.spacing(2),
    },
    height: '100%',
  },
  headerLayoutContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: 500,
      marginRight: theme.spacing(2),
    },
    height: '100%',
  },
  formWrapper: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'flex-start'
    }
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('md')]: {
      alignItems: 'center',
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      alignItems: 'flex-start',
      width: 500,
    },
    height: '100%',
  },
  formLayoutContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: 500,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    height: '100%',
  },
  form: {
    maxWidth: 500
  },
  buttonContainer: {
    marginTop: 16,
    width: '100%',
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  fixedAmountContainer: {
    width: '100%'
  },
  amountHeader: {
    padding: 0
  },
  fixedAmount: {
    fontSize: 20
  },
  errorBanner: {
    width: '100%',
    backgroundColor: red[500],
  },
  errorBannerText: {
    fontSize: 12,
    color: 'white',
    padding: 4,
    paddingLeft: 8,
    paddingRight: 8,
    textAlign: 'center'
  },
  datetimePickerLabel: {
    transform: 'translate(14px, 10px) scale(0.75) !important'
  },
  datetimePickerInput: {
    paddingTop: 26,
    paddingBottom: 11
  },
  continueButtonContainer: {
    marginTop: theme.spacing(1)
  },
  termsContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'flex-end'
  },
  terms: {
    color: theme.palette.text.hint,
  },
  termsCentered: {
    color: theme.palette.text.hint,
    textAlign: 'center'
  },
  link: {
    color: theme.palette.text.hint,
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5)
  },
  incentivesContainer: {
    marginTop: theme.spacing(2)
  },
  title: {
    textAlign: 'center'
  },
  amountSubtitle: {
    marginBottom: theme.spacing(1)
  }
});

const mapStateToProps = (state) => {
  const {locale, shopifyMode, theme, customSettings} = state;
  return {
    locale: locale.resolvedLocale,
    theme: theme.theme,
    shopifyMode: shopifyMode.shopifyMode,
    shopifySettings: shopifyMode.shopifySettings,
    customSettings: customSettings.customSettings
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators({}, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(OrderForm));
